import type {
  ResultatsClasseEtudiantAvecEc,
  ResultatsClasseEtudiantBase,
  ResultatsClasseEtudiantEnrichi,
  ResultatsClassePayload,
} from '@/types/resultatsClasse'
import { firstDecodedNamePart } from '@/utils/decodeHtmlEntities'
import { buildMapByLaravelInscriptionId } from '@/utils/inscriptionIdHelpers'
import { formatMoyenneDisplay } from '@/utils/noteMoyenneHelpers'
import {
  fetchAllEtudiantsBisForSessionSpecialite,
  warnEtudiantsBisTruncated,
  type EtudiantsBisFetcher,
} from '@/utils/etudiantsBisPagination'

export function canDownloadResultatsClasse(
  session: { responsable_pedagogique_principal_id?: number | null; responsable_pedagogique_suppleant_id?: number | null } | null | undefined,
  personnelId: number | undefined | null,
  flags: { isAdmin?: boolean; isSuperAdmin?: boolean }
): boolean {
  if (flags.isAdmin || flags.isSuperAdmin) return true
  if (!session || personnelId == null) return false
  const pid = Number(personnelId)
  const principalId = session.responsable_pedagogique_principal_id
    ? Number(session.responsable_pedagogique_principal_id)
    : null
  const suppleantId = session.responsable_pedagogique_suppleant_id
    ? Number(session.responsable_pedagogique_suppleant_id)
    : null
  return (
    (principalId !== null && principalId === pid && principalId !== 0) ||
    (suppleantId !== null && suppleantId === pid && suppleantId !== 0)
  )
}

function resolveStudentIdentity(
  inscriptionId: number,
  studentMap: Map<number, { nom: string; prenom: string; matricule: string }>
) {
  const info = studentMap.get(inscriptionId)
  if (info) {
    return {
      nom: info.nom,
      prenom: info.prenom,
      matricule: info.matricule || `#${inscriptionId}`,
    }
  }
  return {
    nom: '—',
    prenom: '—',
    matricule: `#${inscriptionId}`,
  }
}

function enrichEtudiantBase(
  row: ResultatsClasseEtudiantBase,
  studentMap: Map<number, { nom: string; prenom: string; matricule: string }>
): ResultatsClasseEtudiantEnrichi {
  const identity = resolveStudentIdentity(row.inscription_id, studentMap)
  return {
    inscription_id: row.inscription_id,
    etudiant_id: row.etudiant_id,
    nom: identity.nom,
    prenom: identity.prenom,
    matricule: identity.matricule,
    moyenne: row.moyenne,
    credits_totaux: row.credits_totaux,
    credits_par_semestre: row.credits_par_semestre ?? [],
  }
}

function enrichEtudiantAvecEc(
  row: ResultatsClasseEtudiantAvecEc,
  studentMap: Map<number, { nom: string; prenom: string; matricule: string }>
): ResultatsClasseEtudiantEnrichi {
  return {
    ...enrichEtudiantBase(row, studentMap),
    ecs: row.ecs ?? [],
  }
}

export function buildStudentMapFromEtudiantsBis(etudiantsBis: any[]) {
  return buildMapByLaravelInscriptionId(etudiantsBis, (student) => ({
    nom: firstDecodedNamePart(student.personne?.nom, student.n_nom) || '—',
    prenom: firstDecodedNamePart(student.personne?.prenom, student.n_prenom) || '—',
    matricule: String(student.matricule || student.code || '').trim(),
  }))
}

export function sortResultatsClasseByName(
  a: ResultatsClasseEtudiantEnrichi,
  b: ResultatsClasseEtudiantEnrichi
) {
  const nameA = `${a.nom} ${a.prenom}`.toLowerCase().trim()
  const nameB = `${b.nom} ${b.prenom}`.toLowerCase().trim()
  return nameA.localeCompare(nameB, 'fr', { numeric: true, sensitivity: 'base' })
}

export function sortResultatsClasseByMatricule(
  a: ResultatsClasseEtudiantEnrichi,
  b: ResultatsClasseEtudiantEnrichi
) {
  return a.matricule.localeCompare(b.matricule, 'fr', { numeric: true, sensitivity: 'base' })
}

export function enrichResultatsClassePayload(
  payload: ResultatsClassePayload,
  etudiantsBis: any[]
) {
  const studentMap = buildStudentMapFromEtudiantsBis(etudiantsBis)

  return {
    ...payload,
    passage_superieur: payload.passage_superieur
      .map((row) => enrichEtudiantBase(row, studentMap))
      .sort(sortResultatsClasseByName),
    admis_avec_rattrapage: payload.admis_avec_rattrapage
      .map((row) => enrichEtudiantAvecEc(row, studentMap))
      .sort(sortResultatsClasseByName),
    redouble: payload.redouble
      .map((row) => enrichEtudiantAvecEc(row, studentMap))
      .sort(sortResultatsClasseByName),
  }
}

export function formatResultatsClasseMoyenne(moyenne: number | null | undefined): string {
  if (moyenne === null || moyenne === undefined) return '—'
  return formatMoyenneDisplay(moyenne)
}

export function formatEcsList(ecs: { code: string; intitule: string }[] | undefined): string {
  if (!ecs?.length) return '—'
  return ecs.map((ec) => ec.intitule).join(' ; ')
}

export function buildResultatsClasseDocumentTitle(payload: ResultatsClassePayload): string {
  const sessionLabel = payload.session.nom || payload.session.code
  const annee = payload.session.annee_universitaire
  const spec = payload.specialite.intitule || payload.specialite.code
  return `RÉSULTATS DE CLASSE — ${sessionLabel}${annee ? ` (${annee})` : ''} — ${spec}`
}

export async function loadEtudiantsBisForSessionSpecialite(
  filterInformation: EtudiantsBisFetcher,
  sessionId: number,
  specialiteId: number
): Promise<any[]> {
  const { results, truncated } = await fetchAllEtudiantsBisForSessionSpecialite(
    filterInformation,
    sessionId,
    specialiteId
  )
  warnEtudiantsBisTruncated(truncated, 'résultats de classe')
  return results
}
